import { useEffect } from "react";
import "@/App.css";
import "@/i18n";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "@/components/ui/sonner";
import { initAnalytics, track, trackPageView } from "@/lib/analytics";

import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import FooterCTA from "@/components/FooterCTA";

import Home from "@/pages/Home";
import Services from "@/pages/Services";
import Team from "@/pages/Team";
import Therabody from "@/pages/Therabody";
import Contact from "@/pages/Contact";
import Book from "@/pages/Book";
import Blog from "@/pages/Blog";
import BlogPost from "@/pages/BlogPost";
import Topic from "@/pages/Topic";
import CorporateWellness from "@/pages/CorporateWellness";
import Learn from "@/pages/Learn";
import TherapistMatch from "@/pages/TherapistMatch";
import SymptomCheck from "@/pages/SymptomCheck";
import TherabodyMatch from "@/pages/TherabodyMatch";
import Pregnancy from "@/pages/Pregnancy";
import ConciergeChat from "@/components/ConciergeChat";
import AdminLogin from "@/pages/admin/Login";
import AdminBlog from "@/pages/admin/BlogDashboard";
import AdminNewsletter from "@/pages/admin/NewsletterDashboard";
import AdminRefresh from "@/pages/admin/RefreshDashboard";
import AdminComments from "@/pages/admin/CommentsModeration";
import AdminSubmissions from "@/pages/admin/Submissions";
import AdminAnalytics from "@/pages/admin/AnalyticsDashboard";
import AdminLearningHub from "@/pages/admin/LearningHubDashboard";
import AdminTherabody from "@/pages/admin/TherabodyDashboard";
import AdminInvoiceGenerator from "@/pages/admin/InvoiceGenerator";
import AdminInvoiceHistory from "@/pages/admin/InvoiceHistory";
import AdminInvoiceClients from "@/pages/admin/InvoiceClients";
import BetaAdminDashboard from "@/pages/admin/BetaAdminDashboard";
import ConciergeAnalyticsDashboard from "@/pages/admin/ConciergeAnalyticsDashboard";
import VoiceDashboard from "@/pages/admin/VoiceDashboard";
import StaffLogin from "@/pages/staff/StaffLogin";
import StaffPortal from "@/pages/staff/StaffPortal";
import FocusPage, { FocusIndex } from "@/pages/FocusPage";
import AdminNav from "@/components/AdminNav";
import BetaGate from "@/components/BetaGate";
import BetaBadge from "@/components/BetaBadge";
import BetaFeedback from "@/components/BetaFeedback";

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
    initAnalytics();
    // Backend funnel + GA4 page_view (whitelisted funnel event).
    track("page_view", {
      page_path: pathname,
      page_location: window.location.href,
      page_title: document.title,
    });
    // Matomo SPA page-view tracking — uses the title set by react-helmet-async
    // on the next tick after route change.
    setTimeout(() => trackPageView(pathname, document.title), 50);
  }, [pathname]);
  return null;
}

function PublicLayout({ children }) {
  return (
    <>
      <Navbar />
      {children}
      <FooterCTA />
      <Footer />
      <ConciergeChat />
      <BetaBadge />
      <BetaFeedback />
    </>
  );
}

function AdminLayout({ children }) {
  const { pathname } = useLocation();
  const showNav = pathname !== "/admin/login" && pathname !== "/staff/login";
  return (
    <>
      {showNav && <AdminNav />}
      {children}
    </>
  );
}

function AppShell() {
  const { pathname } = useLocation();
  const isAdmin = pathname.startsWith("/admin");

  useEffect(() => {
    const onClick = (e) => {
      const a = e.target.closest("a[href]");
      if (!a) return;
      const href = a.getAttribute("href") || "";
      const isBooking =
        href === "/book" ||
        href.endsWith("/book") ||
        href.includes("janeapp.com") ||
        href.includes("pabau.com/online-bookings/") ||
        href.includes("ashlan.clinic/Book-with-");
      if (isBooking) {
        track("book_now_click", {
          destination: href,
          link_text: (a.innerText || "").trim().slice(0, 80),
        });
      }
    };
    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, []);

  return (
    <>
      <ScrollToTop />
      {isAdmin ? (
        <AdminLayout>
          <Routes>
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin/blog" element={<AdminBlog />} />
            <Route path="/admin/newsletters" element={<AdminNewsletter />} />
            <Route path="/admin/refresh" element={<AdminRefresh />} />
            <Route path="/admin/comments" element={<AdminComments />} />
            <Route path="/admin/submissions" element={<AdminSubmissions />} />
            <Route path="/admin/analytics" element={<AdminAnalytics />} />
            <Route path="/admin/learning-hub" element={<AdminLearningHub />} />
            <Route path="/admin/therabody" element={<AdminTherabody />} />
            <Route path="/admin/invoices" element={<AdminInvoiceGenerator />} />
            <Route path="/admin/invoice-history" element={<AdminInvoiceHistory />} />
            <Route path="/admin/invoice-clients" element={<AdminInvoiceClients />} />
            <Route path="/admin/beta" element={<BetaAdminDashboard />} />
            <Route path="/admin/concierge" element={<ConciergeAnalyticsDashboard />} />
            <Route path="/admin/voice" element={<VoiceDashboard />} />
            <Route path="/admin" element={<AdminBlog />} />
          </Routes>
        </AdminLayout>
      ) : (
        <PublicLayout>
          <BetaGate />
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/services" element={<Services />} />
            <Route path="/team" element={<Team />} />
            <Route path="/therabody" element={<Therabody />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/preview/:token" element={<BlogPost previewMode />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/topics/:topicSlug" element={<Topic />} />
            <Route path="/corporate-wellness" element={<CorporateWellness />} />
            <Route path="/learn" element={<Learn />} />
            <Route path="/match" element={<TherapistMatch />} />
            <Route path="/symptom-check" element={<SymptomCheck />} />
            <Route path="/therabody/match" element={<TherabodyMatch />} />
            <Route path="/pregnancy" element={<Pregnancy />} />
            <Route path="/focus" element={<FocusIndex />} />
            <Route path="/focus/:slug" element={<FocusPage />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/book" element={<Book />} />
            <Route path="/staff/login" element={<StaffLogin />} />
            <Route path="/staff" element={<StaffPortal />} />
            <Route path="*" element={<Home />} />
          </Routes>
        </PublicLayout>
      )}
      <Toaster position="top-right" richColors />
    </>
  );
}

function App() {
  return (
    <div className="App">
      <HelmetProvider>
        <BrowserRouter>
          <AppShell />
        </BrowserRouter>
      </HelmetProvider>
    </div>
  );
}

export default App;
